import { Tooltip } from '@affine/component';
import { AnimatedRegion } from '@affine/core/components/animation/animated-region';
import { useI18n } from '@affine/i18n';
import { InformationIcon } from '@blocksuite/icons/rc';
import clsx from 'clsx';
import {
  type AnimationEvent,
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { ReadinessState, ReadinessTileId } from '../automation-readiness';
import * as styles from '../automation-settings-panel.css';
import { paneOfTile } from '../settings-pane';
import { attentionPulseOnce } from './attention-pulse.css';

/**
 * Set inside a `SettingSection`: the section already shows the setting's
 * title and help, so the first `CollapsibleSection` below it renders flat
 * (no own header, always open) and hands its (i) help action up here.
 */
export interface SettingSectionFrame {
  registerInfo: (onInfoClick: (() => void) | null) => void;
}

export const HiddenSettingsContext = createContext<
  ReadonlySet<ReadinessTileId>
>(new Set());

/** Each setting's readiness, so its section names its state in words. */
export const SettingStatesContext = createContext<
  Partial<Record<ReadinessTileId, ReadinessState>>
>({});

const STATE_LABEL_KEYS: Record<ReadinessState, string> = {
  ok: 'com.streamlain.automation.panel.state.ok',
  missing: 'com.streamlain.automation.panel.state.missing',
  error: 'com.streamlain.automation.panel.state.error',
  none: 'com.streamlain.automation.panel.state.none',
};

/**
 * Set by the settings panel: hides a setting that the user switched off, so
 * it waits among the unused settings again (e.g. status tracking).
 */
export const ConcealSettingContext = createContext<
  (tile: ReadinessTileId) => void
>(() => {});

export const useConcealSetting = () => useContext(ConcealSettingContext);

/**
 * Set by the settings panel: the setting an open item led to. Its section
 * sends the attention pulse, and so does the action inside it that settles
 * the item (`data-attention-action`, e.g. auto-match).
 */
export const SettingAttentionContext = createContext<{
  tile: ReadinessTileId | null;
  onEnd?: () => void;
}>({ tile: null });

export const SettingSectionFrameContext =
  createContext<SettingSectionFrame | null>(null);

export const useSettingSectionFrame = () =>
  useContext(SettingSectionFrameContext);

/**
 * One setting in the settings panel's form: the setting's name (the same as
 * its nav item) with its (i) help right behind it, the state at the top
 * right, then the setting UI. The help text stays out of the scan path: it
 * is the (i) tooltip and the section's description. A click on the (i)
 * keeps the help open; a setting with a tour starts the tour instead. The nav item with the same id scrolls to it
 * and outlines it on hover (`data-setting`).
 */
export const SettingSection: FC<{
  tile: ReadinessTileId;
  /** Kept for tutorials and highlights that target the legacy section ids. */
  section?: string;
  /** Extra buttons next to the title (e.g. "View" for the workflow). */
  headerActions?: ReactNode;
  children: ReactNode;
}> = ({ tile, section, headerActions, children }) => {
  const t = useI18n();
  const titleId = useId();
  const infoRef = useRef<(() => void) | null>(null);
  const [hasInfo, setHasInfo] = useState(false);
  const registerInfo = useCallback((onInfoClick: (() => void) | null) => {
    infoRef.current = onInfoClick;
    setHasInfo(onInfoClick !== null);
  }, []);
  const frame = useMemo(() => ({ registerInfo }), [registerInfo]);
  // The help shows on hover and stays open after a click, until the next
  // click, Escape or a click elsewhere.
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const [helpHovered, setHelpHovered] = useState(false);
  const [helpPinned, setHelpPinned] = useState(false);
  const showHelp = useCallback(() => {
    const startTour = infoRef.current;
    if (startTour) {
      setHelpPinned(false);
      startTour();
      return;
    }
    setHelpPinned(pinned => !pinned);
  }, []);
  const title = t.t(`com.streamlain.automation.panel.tile.${tile}`);
  const help = t.t(`com.streamlain.automation.panel.help.${tile}`);
  const hidden = useContext(HiddenSettingsContext).has(tile);
  const state = useContext(SettingStatesContext)[tile];
  const attention = useContext(SettingAttentionContext);
  const attended = attention.tile === tile;
  const endAttention = (event: AnimationEvent<HTMLElement>) => {
    if (attended && event.target === event.currentTarget) attention.onEnd?.();
  };
  // "Optional" names an unused optional step setting; the general settings
  // (history) are never optional.
  const shownState =
    state === 'none' && paneOfTile(tile) === 'general' ? undefined : state;

  // Added or switched off in place, the setting grows in or out.
  return (
    <AnimatedRegion expanded={!hidden} className={styles.settingRegion}>
      <section
        className={clsx(styles.settingSection, attended && attentionPulseOnce)}
        data-setting={tile}
        data-attention={attended || undefined}
        onAnimationEnd={endAttention}
        data-section={section}
        aria-labelledby={titleId}
        aria-description={help}
      >
        <div className={styles.settingHeader}>
          <div className={styles.settingHeaderText}>
            <h4 id={titleId} className={styles.settingTitle}>
              {title}
            </h4>
            <Tooltip
              content={help}
              rootOptions={{
                open: helpPinned || helpHovered,
                onOpenChange: setHelpHovered,
              }}
              options={{
                onEscapeKeyDown: () => setHelpPinned(false),
                // A press on the (i) itself is its click, which toggles.
                onPointerDownOutside: event => {
                  if (!infoButtonRef.current?.contains(event.target as Node))
                    setHelpPinned(false);
                },
              }}
            >
              <button
                ref={infoButtonRef}
                type="button"
                className={styles.settingInfo}
                data-testid="setting-info"
                data-tour={hasInfo || undefined}
                data-pinned={helpPinned || undefined}
                onClick={showHelp}
                onBlur={() => setHelpPinned(false)}
                aria-label={title}
                aria-description={help}
                aria-expanded={hasInfo ? undefined : helpPinned}
              >
                <InformationIcon width={16} height={16} />
              </button>
            </Tooltip>
          </div>
          {(headerActions || shownState) && (
            <div className={styles.settingHeaderActions}>
              {headerActions}
              {shownState && (
                <span
                  className={styles.settingState}
                  data-state={shownState}
                  data-testid="setting-state"
                >
                  {t.t(STATE_LABEL_KEYS[shownState])}
                </span>
              )}
            </div>
          )}
        </div>
        <SettingSectionFrameContext.Provider value={frame}>
          {children}
        </SettingSectionFrameContext.Provider>
      </section>
    </AnimatedRegion>
  );
};

/**
 * Settings that belong together (e.g. the primary table and what is read
 * from it), framed like the sidebar cards. Hidden with its last shown
 * setting; a unit without tiles (sharing in the Hub) always shows.
 */
export const SettingUnit: FC<{
  name: string;
  tiles?: readonly ReadinessTileId[];
  children: ReactNode;
}> = ({ name, tiles = [], children }) => {
  const hidden = useContext(HiddenSettingsContext);
  const shown = tiles.length === 0 || tiles.some(tile => !hidden.has(tile));
  return (
    <AnimatedRegion expanded={shown} className={styles.settingRegion}>
      <div className={styles.settingUnit} data-setting-unit={name}>
        {children}
      </div>
    </AnimatedRegion>
  );
};
